/** 手机使用要求：记录按字段阅读，金额/薪资对比保留横向表；弹窗表单可换行、主要操作便于触控。只调整显示，不改变数据、权限及审批规则。 */
import React, { useState } from "react";
import { useApp } from "../../context/AppContext";
import { PAY_STATUS, payStageByKey } from "../../data/constants";
import { fmtMoney } from "../../lib/appUtils";
import { Popover, PopoverTrigger, PopoverContent } from "../ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { PaymentRecordDialog } from "../PaymentRecordDialog";
import { ink } from "../../lib/ink";
import { safeHtml } from "../../lib/safeHtml";
import { Send, CheckCircle2, FileText, FileDown } from "lucide-react";

/**
 * ReceivablePopover —— 「收款状态」摘要胶囊 + 明细浮窗。
 *
 * 需求背景（2026-10-13）：
 *   财务项目列表（/finance/projects）原来有两套列表 ——「应收项目 · 与施工进度同步」（来自 /projects 的
 *   visibleProjects，逐付款期次可开单/收款）和「所有项目列表」（来自 /finance/projects-overview，财务总览）。
 *   产品决定把两者合并成「所有项目列表」一张表，并在表里新增「收款状态」列承载原应收列表的能力。
 *
 *   为了不破坏表格行高统一，本组件把逐期次明细收进「点击弹出的浮窗」：
 *     - 主表单元格只渲染一行摘要（待开单 ×N · 已开票 ×M / 已收清）；
 *     - 点击摘要弹出浮窗，浮窗内逐付款期次展示「期次名 · 金额 · 状态」并在右侧给出对应操作按钮。
 *
 *   之所以用「点击」而非「悬停」触发：浮窗内含「开单 / 标记已收」等可交互按钮，悬停触发时鼠标一旦
 *   移出触发区浮窗即关闭，按钮根本点不到；点击触发（Radix Popover）稳定且触屏可用。
 *
 * 数据来源：project.payments（由调用方传入，取自 useApp().visibleProjects，即 /projects 的全量项目）。
 * 操作接口：开单 / 标记已收走 useApp().markInvoiced / markPaid（内部会 refresh 全局项目数据）。
 */
export default function ReceivablePopover({ project, api, zh, lang, onChanged }) {
  const { markInvoiced, markPaid, user } = useApp();
  const [open, setOpen] = useState(false);
  const [payRec, setPayRec] = useState(null);
  const [preview, setPreview] = useState(null);
  const [sendingStmt, setSendingStmt] = useState(false);
  const canAct = ["admin", "finance", "ceo"].includes(user?.role);

  const payments = project?.payments || [];
  // 待处理期次（待开单 / 已开票）排在前面，已收期次垫底 —— 与旧「应收项目」列表的展示顺序保持一致。
  const actionable = payments.filter((x) => x.status === "pending_invoice" || x.status === "invoiced");
  const paidPays = payments.filter((x) => x.status === "paid");
  const pendingCnt = payments.filter((x) => x.status === "pending_invoice").length;
  const invoicedCnt = payments.filter((x) => x.status === "invoiced").length;
  const openCnt = pendingCnt + invoicedCnt;

  // 下载该期次收款单 PDF（与旧应收列表同一接口）
  const downloadPdf = async (stage) => {
    try {
      const res = await api.get(`/projects/${project.id}/invoice-pdf`, { params: { stage, lang }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `invoice_${project.id}_${stage}.pdf`; document.body.appendChild(a); a.click();
      a.remove(); window.URL.revokeObjectURL(url);
    } catch { alert(zh ? "PDF 生成失败" : "PDF failed"); }
  };

  // 发送整项目对账单（邮件/短信附安全链接）
  const sendStatement = async () => {
    if (!window.confirm(zh ? `向客户 ${project.clientName || ""} 发送「${project.address}」的对账单 Statement（邮件/短信附安全查看链接）？` : `Send statement for ${project.address} via email/SMS?`)) return;
    setSendingStmt(true);
    try {
      const { data } = await api.post(`/projects/${project.id}/statement-send`);
      const parts = [];
      if (data.email) parts.push(zh ? "邮件已发送(含PDF附件)" : "email sent");
      if (data.sms) parts.push(zh ? "短信已发送" : "SMS sent");
      if (!data.email && !data.sms) parts.push(zh ? (data.noEmail && data.noPhone ? "客户无邮箱/电话，仅生成链接" : "通道未配置或发送失败，链接已生成") : "link generated only");
      if (data.link) { try { await navigator.clipboard.writeText(data.link); parts.push(zh ? "安全链接已复制" : "link copied"); } catch { parts.push(data.link); } }
      alert((zh ? "对账单：" : "Statement: ") + parts.join(" · "));
    } catch (e) { alert(e?.response?.data?.detail || e.message); }
    setSendingStmt(false);
  };

  // 已收清 / 无待收款：不弹浮窗，直接给一个静态徽标（保持主表行高统一）
  if (openCnt === 0) {
    return (
      <span data-testid={`recv-clear-${project?.id}`} className="text-[11px] font-medium px-2.5 py-0.5 rounded-full bg-slate-100 text-slate-500 whitespace-nowrap">
        {zh ? "已收清" : "Cleared"}
      </span>
    );
  }

  const row = (pay, i) => {
    const st = payStageByKey(pay.stage);
    const label = st ? (zh ? st.zh : st.en) : (pay.label || pay.stage);
    const meta = PAY_STATUS[pay.status];
    const isPaid = pay.status === "paid";
    // 金额口径（与旧「应收项目」列表一致）：待收期次显示计划金额；已收期次显示**实收金额**（paidAmount 优先）
    const amt = isPaid ? (pay.paidAmount ?? pay.amount) : pay.amount;
    // 状态后的小字说明（旧列表有，合并后不能丢）：
    //   已收   → 收款日期 · 方式 · #支票号 · 登记人
    //   已开票 → 开票日期（MM-DD）
    const sub = isPaid
      ? [pay.paidAt, pay.method, pay.reference ? `#${pay.reference}` : "", pay.recordedBy].filter(Boolean).join(" · ")
      : (pay.status === "invoiced" && pay.invoicedAt ? pay.invoicedAt.slice(5) : "");
    return (
      <div key={`${pay.stage}-${i}`} className="flex-wrap md:flex-nowrap flex items-center gap-2 py-1.5 border-b border-slate-100 last:border-0" data-testid={isPaid ? `paid-record-${project.id}-${pay.stage}` : undefined}>
        <span className={`text-[11px] w-20 shrink-0 truncate ${isPaid ? "text-slate-400" : "text-slate-500"}`}>{label}</span>
        <span className={`text-xs shrink-0 ${isPaid ? "text-slate-400" : "text-slate-700"}`}>{fmtMoney(amt)}</span>
        <span className="text-[11px] px-2 py-0.5 rounded-full whitespace-nowrap shrink-0" style={isPaid ? ink("#64748b", "#f1f5f9") : ink(meta?.color, meta?.bg)}>
          {isPaid ? (zh ? "已收" : "Paid") : pay.status === "pending_invoice" ? (zh ? "待开单" : "Awaiting invoice") : (zh ? "已发出收款单" : "Invoice sent")}
        </span>
        {sub && <span className="text-[11px] text-slate-400 truncate" title={sub}>{sub}</span>}
        <span className="ml-auto flex items-center gap-1.5 shrink-0">
          {pay.status === "pending_invoice" && canAct && (
            <button data-testid={`pop-invoice-${project.id}-${pay.stage}`} onClick={() => setPreview({ pay })}
              className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-[11px] text-white bg-blue-600 hover:bg-blue-700 px-2 py-1 rounded-md flex items-center gap-1"><Send className="h-3 w-3" />{zh ? "开单" : "Invoice"}</button>
          )}
          {pay.status === "invoiced" && canAct && (
            <button data-testid={`pop-paid-${project.id}-${pay.stage}`} onClick={() => setPayRec({ pay })}
              className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-[11px] text-white bg-green-600 hover:bg-green-700 px-2 py-1 rounded-md flex items-center gap-1"><CheckCircle2 className="h-3 w-3" />{zh ? "标记已收" : "Mark Paid"}</button>
          )}
          {pay.status === "invoiced" && (
            <button data-testid={`pop-pdf-${project.id}-${pay.stage}`} onClick={() => downloadPdf(pay.stage)}
              className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-[11px] text-slate-600 border border-slate-200 hover:border-brand-300 hover:text-brand-500 px-2 py-1 rounded-md flex items-center gap-1"><FileDown className="h-3 w-3" />PDF</button>
          )}
        </span>
      </div>
    );
  };

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button data-testid={`recv-chip-${project?.id}`}
            className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full border border-brand-200 bg-brand-50 text-brand-700 hover:bg-brand-100 transition-colors whitespace-nowrap">
            {pendingCnt > 0 && (
              <span className="text-[11px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700">{zh ? `待开单 ×${pendingCnt}` : `Pending ×${pendingCnt}`}</span>
            )}
            {invoicedCnt > 0 && (
              <span className="text-[11px] font-semibold px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700">{zh ? `已开票 ×${invoicedCnt}` : `Invoiced ×${invoicedCnt}`}</span>
            )}
          </button>
        </PopoverTrigger>
        {/* 宽度 560px：容纳「期次名 · 金额 · 状态徽标 + 标记已收 + PDF」这一整行（440px 时首行会被裁）。
            max-w + collisionPadding 兜底窄屏：浮窗靠近视口边缘时 Radix 会自动避让/收窄。 */}
        <PopoverContent align="start" side="bottom" collisionPadding={16} className="w-[560px] max-w-[calc(100vw-2rem)] max-h-[70vh] overflow-y-auto p-3" data-testid={`recv-pop-${project?.id}`}>
          <div className="flex-wrap md:flex-nowrap flex items-center justify-between mb-2">
            <div className="min-w-0">
              <p className="text-[13px] font-semibold text-slate-900 truncate">{zh ? "收款进度" : "Collections"} · {project?.address}</p>
              <p className="text-[11px] text-slate-400">{payments.length} {zh ? "期" : "milestones"} · {zh ? "待处理" : "open"} {openCnt}</p>
            </div>
            <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-600 text-sm font-bold px-1 shrink-0">✕</button>
          </div>
          <div>{[...actionable, ...paidPays].map(row)}</div>
          {canAct && (
            <div className="flex justify-end mt-2 pt-2 border-t border-slate-100">
              <button data-testid={`pop-statement-${project.id}`} onClick={sendStatement} disabled={sendingStmt}
                className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-[11px] text-slate-600 border border-slate-200 hover:border-emerald-400 hover:text-emerald-700 px-2 py-1 rounded-md flex items-center gap-1 disabled:opacity-50">
                <FileText className="h-3 w-3" />{sendingStmt ? (zh ? "发送中…" : "Sending…") : (zh ? "发对账单" : "Send Statement")}
              </button>
            </div>
          )}
        </PopoverContent>
      </Popover>

      {payRec && (
        <PaymentRecordDialog zh={zh} stageLabel={(() => { const st = payStageByKey(payRec.pay.stage); return st ? (zh ? st.zh : st.en) : payRec.pay.stage; })()}
          defaultAmount={payRec.pay.amount}
          onConfirm={async (rec) => { await markPaid(project, payRec.pay, rec); setPayRec(null); onChanged?.(); }}
          onClose={() => setPayRec(null)} />
      )}
      {preview && (
        <InvoicePreview
          info={{ project, pay: preview.pay }}
          api={api}
          lang={lang}
          onClose={() => setPreview(null)}
          onConfirm={() => { markInvoiced(project, preview.pay); setPreview(null); onChanged?.(); }}
          onDownload={() => downloadPdf(preview.pay.stage)}
        />
      )}
    </>
  );
}

/**
 * InvoicePreview —— 开单前的收款单预览弹窗（从 CollectionsWorkbench 抽出，随收款操作一起复用）。
 * 拉取 /projects/{id}/invoice-preview 展示收件客户、应收金额与邮件 HTML 预览，确认后标记为「已开票」。
 */
function InvoicePreview({ info, api, lang, onClose, onConfirm, onDownload }) {
  const { project, pay } = info;
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  React.useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/projects/${project.id}/invoice-preview`, { params: { stage: pay.stage, lang } });
        if (alive) { setData(data); setLoading(false); }
      } catch { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [project.id, pay.stage, lang]); // eslint-disable-line
  const noEmail = data && !data.to;
  const zh = lang === "zh";
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="invoice-preview-dialog">
        <DialogHeader><DialogTitle>{zh ? "收款单预览" : "Invoice Preview"}</DialogTitle></DialogHeader>
      {loading && <p className="text-sm text-slate-400 py-6 text-center">{zh ? "加载中…" : "Loading…"}</p>}
      {data && (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="bg-slate-50 rounded-lg px-3 py-2"><p className="text-[11px] text-slate-400">{zh ? "收件客户" : "Client"}</p><p className="font-medium text-slate-800 mt-0.5">{data.clientName || "-"}</p></div>
            <div className="bg-slate-50 rounded-lg px-3 py-2"><p className="text-[11px] text-slate-400">{zh ? "客户邮箱" : "Email"}</p><p className={`font-medium mt-0.5 ${noEmail ? "text-red-600" : "text-slate-800"}`} data-testid="preview-to">{data.to || (zh ? "未填写邮箱" : "no email")}</p></div>
          </div>
          <div className="flex-wrap md:flex-nowrap bg-red-50 rounded-lg px-3 py-2 flex items-center justify-between">
            <span className="text-sm text-slate-600">{zh ? "应收金额" : "Amount Due"}</span>
            <span className="text-lg font-bold text-red-600" data-testid="preview-amount">${Number(data.amount || 0).toLocaleString("en-US")}</span>
          </div>
          <div>
            <p className="text-xs font-semibold text-slate-500 mb-1">{zh ? "邮件内容预览" : "Email Preview"}</p>
            <div className="border border-slate-200 rounded-lg overflow-hidden max-h-72 overflow-y-auto" data-testid="preview-html" dangerouslySetInnerHTML={safeHtml(data.html)} />
          </div>
          {noEmail && <p className="text-xs text-red-600">{zh ? "该项目未填写客户邮箱，将只记录为已开单，不会发送邮件。" : "No client email — will be marked invoiced without sending."}</p>}
          <div className="flex-wrap md:flex-nowrap flex justify-end gap-2 pt-1">
            <Button type="button" variant="outline" onClick={onClose}>{zh ? "取消" : "Cancel"}</Button>
            <Button type="button" variant="outline" onClick={onDownload} data-testid="preview-download-pdf-btn"><FileDown className="h-4 w-4 mr-1" />PDF</Button>
            <Button type="button" onClick={onConfirm} data-testid="confirm-send-invoice-btn" className="bg-blue-600 hover:bg-blue-700 text-white"><Send className="h-4 w-4 mr-1" />{zh ? "确认发送" : "Confirm & Send"}</Button>
          </div>
        </div>
      )}
      </DialogContent>
    </Dialog>
  );
}
